import React, { useMemo, useContext } from 'react'
import { useIntl } from 'react-intl'
import clsx from 'clsx'
import { rem } from 'polished'
import { connect } from 'react-redux'
import styled from 'styled-components'
import getCheckoutSignUpOfferDetails from 'checkout/selectors/payment/getCheckoutSignUpOfferDetails'
import PriceRowMerchandisePriceStack from 'LuxKit/PricePoints/Merchandise/PriceRowMerchandisePriceStack'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import Image from 'components/Common/Image'
import Group from 'components/utils/Group'

import { mediaQueryDown, mediaQueryUp } from 'components/utils/breakpoint'
import PriceRowPriceWithCaption from 'LuxKit/PricePoints/PriceRowPriceWithCaption'
import LoyaltyPointsEarn from 'loyalty/components/Earn/LoyaltyPointsEarn'
import { generateLoyaltyPointsCalculatorHotelOptions, generateLoyaltyPointsCalculatorBundledFlightOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import GeoContext from 'contexts/geoContext'

const Container = styled.div`
  display: flex;
  border: 1px solid ${(props) => props.theme.palette.neutral.default.five};
  gap: ${rem(12)};
  padding: ${rem(12)};

  ${mediaQueryUp.tablet} {
    padding: 0;
  }
`

const TextBlock = styled(Group)`
  padding: 0;

  ${mediaQueryUp.tablet} {
    padding: ${rem(12)};
    padding-right: 0;
  }
`

const ImageBlock = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  width: auto;
  height: ${rem(96)};
  overflow: hidden;

  ${mediaQueryDown.mobile} {
    width: ${rem(68)};
    height: ${rem(68)};
  }

  img {
    height: 100%;
    width: 100%;
    max-width: ${rem(170)};
    object-fit: cover;
  }

  &.image-padding {
    padding: ${rem(12)};
    padding-left: 0;

    ${mediaQueryDown.mobile} {
      padding: 0;
    }
  }
`

interface Props {
  offerDetails?: App.AccountModalOfferDetails
  hotelOfferDetails?: App.AccountModalOfferDetails
  checkoutOfferDetails?: App.AccountModalOfferDetails
}

function AccountAccessOfferPreview(props: Props) {
  const intl = useIntl()
  const { hotelOfferDetails, checkoutOfferDetails, offerDetails } = props
  const signUpOfferDetails = hotelOfferDetails || checkoutOfferDetails || offerDetails
  const offer = signUpOfferDetails?.offer
  const packageView = signUpOfferDetails?.packageView
  const saleUnit = 'total'
  const total = signUpOfferDetails?.value ?? 0
  const hotelTotal = total + (packageView?.propertyFees ?? 0)
  const { currentRegionCode } = useContext(GeoContext)

  const pointsEarnCalculationRequests = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(() => [
    generateLoyaltyPointsCalculatorHotelOptions(offer, { packageView, currentRegionCode, nights: packageView?.package?.duration }),
    generateLoyaltyPointsCalculatorBundledFlightOptions(offer, {
      packageView,
      skip: !signUpOfferDetails?.isBundledWithFlights,
    }),
  ], [offer, packageView, signUpOfferDetails?.isBundledWithFlights, currentRegionCode])

  if (!signUpOfferDetails) return null

  let priceCaption = signUpOfferDetails.durationLabel ?? ''
  if (!signUpOfferDetails.durationLabel && signUpOfferDetails.duration) {
    priceCaption = signUpOfferDetails.isBundledWithFlights
      ? intl.formatMessage(
          {
            id: '41XSdnhmY_alsWGQC5bc',
            defaultMessage: '{count, plural, one {# night} other {# nights}} + flights from',
            description: 'Duration in nights with flights for account access offer preview',
          },
          { count: signUpOfferDetails.duration },
        )
      : intl.formatMessage(
          {
            id: 'i76S0_kbXfId10A_M-zf',
            defaultMessage: '{count, plural, one {# night} other {# nights}} from',
            description: 'Duration in nights followed by "from" for account access offer preview',
          },
          { count: signUpOfferDetails.duration },
        )
  }

  return (
    <Container>
      <TextBlock direction="vertical" gap={4} fullWidth>
        <BodyTextBlock variant="medium" weight="bold" lineClamp={2}>{signUpOfferDetails.title}</BodyTextBlock>
        {signUpOfferDetails.value > 0 && !signUpOfferDetails.merchandiseVariant && (
          <PriceRowPriceWithCaption
            size="S"
            caption={priceCaption}
            price={hotelTotal}
            saleUnit={saleUnit}
            discountPercentage={signUpOfferDetails.discountPercentage}
          />
        )}
        {signUpOfferDetails.merchandiseVariant && (
          <PriceRowMerchandisePriceStack
            variant={signUpOfferDetails.merchandiseVariant}
            quantity={signUpOfferDetails.quantity ?? 1}
            direction="horizontal"
            size="S"
          />
        )}
        <LoyaltyPointsEarn calculationRequests={pointsEarnCalculationRequests} calculationType="estimate" />
      </TextBlock>
      <ImageBlock className={clsx({ 'image-padding': signUpOfferDetails.addImagePadding })}>
        <Image
          id={signUpOfferDetails.imageId}
          image={signUpOfferDetails.image}
          alt={signUpOfferDetails.imageTitle}
          dpr={2}
          fit={signUpOfferDetails.addImagePadding ? undefined : 'center'}
        />
      </ImageBlock>
    </Container>
  )
}

function mapStateToProps(state: App.State) {
  return {
    checkoutOfferDetails: getCheckoutSignUpOfferDetails(state),
  }
}

export default connect(mapStateToProps)(AccountAccessOfferPreview)
